import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KpiStrip from './KpiStrip';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
const TOOLTIP = { contentStyle: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '12px', direction: 'rtl' } };
const PIE_COLORS = ['#1E40AF', '#059669', '#F59E0B', '#DC2626', '#8B5CF6', '#EC4899'];
const ABC_COLORS = { A: 'bg-emerald-100 text-emerald-700', B: 'bg-amber-100 text-amber-700', C: 'bg-gray-100 text-gray-600' };

export default function ProductsReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('revenue');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get('/reports/products', { date_from: dateFrom, date_to: dateTo });
    if (res?.success) {
      const d = res.data || {};
      const items = d.products || d.items || [];
      // Pre-sort once for each mode
      const by_revenue = [...items].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
      const by_qty = [...items].sort((a, b) => (b.qty || 0) - (a.qty || 0));
      setData({ ...d, by_revenue, by_qty });
    } else {
      setError(res?.error || 'فشل تحميل تقرير المنتجات');
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  if (error) return (
    <div className="p-6 text-center space-y-3">
      <p className="text-red-500">{error}</p>
      <Button onClick={load} variant="outline" size="sm"><RotateCcw className="w-4 h-4 ml-1" />إعادة</Button>
    </div>
  );

  const kpis = data ? [
    { label: 'منتجات مباعة', value: fmt(data.kpis.total_products_sold), suffix: 'منتج', to: '/products' },
    { label: 'إجمالي المبيعات', value: fmt(data.kpis.total_revenue), suffix: cur(), to: '/orders' },
    { label: 'إجمالي الكمية', value: fmt(data.kpis.total_qty), suffix: 'وحدة', to: '/products' },
  ] : [];

  const chartData = mode === 'revenue' ? (data?.by_revenue || []).slice(0, 10) : (data?.by_qty || []).slice(0, 10);

  return (
    <div className="space-y-4">
      <KpiStrip kpis={kpis} loading={loading && !data} />

      <div className="bg-card border border-border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-foreground">أفضل المنتجات</h3>
          <div className="flex bg-muted rounded-lg p-0.5">
            <button onClick={() => setMode('revenue')} className={`px-2 py-1 rounded text-xs font-semibold ${mode === 'revenue' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>إيراد</button>
            <button onClick={() => setMode('qty')} className={`px-2 py-1 rounded text-xs font-semibold ${mode === 'qty' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>كمية</button>
          </div>
        </div>
        {loading && !data ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> :
          chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 36)}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => mode === 'revenue' ? (v / 1000) + 'k' : v} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [mode === 'revenue' ? fmt(v) + ` ${cur()}` : fmt(v) + ' وحدة']} />
                <Bar dataKey={mode === 'revenue' ? 'revenue' : 'qty'} fill="#1E40AF" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-muted-foreground py-8">لا توجد بيانات</p>
        }
      </div>

      {data?.by_category?.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">المبيعات حسب الفئة</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data.by_category} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={70}
                label={({ name, percent }) => percent > 0.05 ? `${name} ${Math.round(percent * 100)}%` : ''} labelLine={false}>
                {data.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {data?.abc?.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-1">تحليل ABC (باريتو)</h3>
          <p className="text-xs text-muted-foreground mb-3">A = أفضل 80% إيراد، B = 80-95%، C = الباقي</p>
          <div className="space-y-1.5">
            {data.abc.slice(0, 15).map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-2">
                <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${ABC_COLORS[p.abc]}`}>{p.abc}</span>
                <p className="text-xs font-medium text-foreground flex-1 truncate">{p.name}</p>
                <span className="text-xs text-muted-foreground">{p.cum_pct}%</span>
                <span className="text-xs font-bold text-primary whitespace-nowrap">{fmt(p.revenue)} {cur()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.stock?.filter(p => p.stock <= 0).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <h3 className="font-bold text-red-700 mb-2">⚠️ منتجات نافدة المخزون ({data.stock.filter(p => p.stock <= 0).length})</h3>
          <div className="space-y-1">
            {data.stock.filter(p => p.stock <= 0).slice(0, 10).map(p => (
              <div key={p.id} className="flex justify-between text-xs">
                <span className="font-medium text-red-700">{p.name}</span>
                <span className="text-red-500 font-bold">{p.stock} وحدة</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}